import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Dropdown, Form, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd';
import { EllipsisOutlined, UserAddOutlined } from '@ant-design/icons';

import { useAccess } from '../../auth/AccessContext.jsx';
import ActionIcon from '../../components/ActionIcon.jsx';
import StatusTag from '../../components/StatusTag.jsx';
import { useLiveTopic } from '../../live/LiveContext.js';
import { STATUS_KIND } from '../../status/statusHelp.js';

const { Text } = Typography;

const STATUS_COLOUR = Object.freeze({ INVITED: 'gold', ACTIVE: 'green', SUSPENDED: 'red' });
const MFA_LABEL = Object.freeze({ ACTIVE: 'Set up', PENDING: 'Being set up', CONFIRMED: 'Confirmed', NONE: 'Not set up' });

const NAME_RULES = (label) => [
    { required: true, whitespace: true, message: label + ' is required.' },
    { max: 50, message: label + ' is at most 50 characters.' },
    { pattern: /^[\p{L}][\p{L}\p{M} '-]*$/u, message: 'Letters, spaces, apostrophes and hyphens only.' }
];

const at = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Asia/Colombo', dateStyle: 'medium' }) : '—');

/** The reason-carrying actions: what to ask, and the call to make. */
const REASONED = Object.freeze({
    resetMfa: {
        title: 'Reset their authenticator?',
        body: 'Their authenticator stops working and they are signed out everywhere. At their next sign-in they scan a new QR code.',
        ok: 'Reset',
        call: (api, uid, reason) => api.access.resetMfa(uid, reason),
        done: 'Authenticator reset. They set up a new one at their next sign-in.'
    },
    suspend: {
        title: 'Suspend this user?',
        body: 'They are signed out at once and cannot sign in until they are reinstated.',
        ok: 'Suspend',
        call: (api, uid, reason) => api.access.suspend(uid, reason),
        done: 'User suspended and signed out.'
    },
    reinstate: {
        title: 'Reinstate this user?',
        body: 'They can sign in again, with the roles they held.',
        ok: 'Reinstate',
        call: (api, uid, reason) => api.access.reinstate(uid, reason),
        done: 'User reinstated.'
    }
});

/**
 * The people who may use the Console (IP-78). An administrator invites a person
 * by email - the user pool emails them a temporary password - changes their
 * roles, resends an invitation, resets their authenticator, suspends and
 * reinstates them. Never their own account: the server refuses it, and this
 * page does not offer it.
 */
const UsersPanel = () => {
    const { api, principal } = useAccess();
    const { message } = App.useApp();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const [inviting, setInviting] = useState(false);
    const [editing, setEditing] = useState(null);
    const [reasoned, setReasoned] = useState(null); // { kind, user }
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState(null);
    const [inviteForm] = Form.useForm();
    const [rolesForm] = Form.useForm();
    const [reasonForm] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [{ users: list }, { roles: defs }] = await Promise.all([api.access.users(), api.access.roles()]);
            setUsers(list);
            setRoles(defs);
            setRefreshedAt(new Date());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [api]);
    // Live screens (docs/live-sync/implementation-plan.md 4.6): re-read, exactly as Refresh does, when
    // another administrator invited, suspended, reinstated or changed someone.
    useLiveTopic(['access'], load);

    useEffect(() => {
        load();
    }, [load]);

    const roleOptions = roles.filter((r) => !r.retiredAt).map((r) => ({ value: r.role, label: r.name + ' (' + r.role + ')' }));

    const run = async (work, done, close) => {
        setBusy(true);
        setDialogError(null);
        try {
            await work();
            message.success(done);
            close();
            await load();
        } catch (e) {
            setDialogError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const invite = (values) => run(
        () => api.access.invite({ email: values.email.trim().toLowerCase(), firstName: values.firstName.trim(), lastName: values.lastName.trim(), roles: values.roles }),
        'Invitation sent to ' + values.email.trim().toLowerCase() + '.',
        () => {
            setInviting(false);
            inviteForm.resetFields();
        }
    );

    const saveRoles = ({ roles: chosen }) => run(
        () => api.access.setRoles(editing.uid, chosen),
        'Roles updated for ' + editing.displayName + '. They apply from their next request.',
        () => setEditing(null)
    );

    const confirmReasoned = ({ reason }) => {
        const spec = REASONED[reasoned.kind];
        return run(() => spec.call(api, reasoned.user.uid, reason.trim()), spec.done, () => {
            setReasoned(null);
            reasonForm.resetFields();
        });
    };

    const resend = async (user) => {
        try {
            await api.access.resendInvite(user.uid);
            message.success('A new invitation was sent to ' + user.email + '.');
        } catch (e) {
            message.error(e.message);
        }
    };

    /**
     * Every action, on every row - an action that cannot be taken is shown
     * disabled with the reason, never hidden (spec 11). The server refuses
     * the same things whatever this menu offers.
     */
    const actionsFor = (user) => {
        const self = user.uid === principal.uid;
        const item = (key, label, why, extra = {}) => ({
            key,
            disabled: Boolean(why),
            ...extra,
            label: why
                ? <span>{label}<br /><Text type="secondary" style={{ fontSize: 11.5 }}>{why}</Text></span>
                : label
        });
        const selfReason = self ? 'Another administrator must change your own account.' : null;
        const items = [item('roles', 'Change roles', selfReason)];
        if (user.status === 'INVITED') items.push(item('resend', 'Resend invitation', selfReason));
        items.push(item('resetMfa', 'Reset authenticator', selfReason
            ?? (user.mfa === 'NONE' ? 'No authenticator yet - it is set up at their first sign-in.' : null)));
        if (user.status === 'SUSPENDED') {
            items.push(item('reinstate', 'Reinstate', selfReason));
        } else {
            items.push(item('suspend', 'Suspend', selfReason, { danger: !selfReason }));
        }
        return items;
    };

    const onAction = (user, key) => {
        setDialogError(null);
        if (key === 'roles') {
            setEditing(user);
            rolesForm.setFieldsValue({ roles: user.roles });
        } else if (key === 'resend') {
            resend(user);
        } else {
            setReasoned({ kind: key, user });
        }
    };

    const columns = [
        {
            title: 'Name',
            key: 'name',
            width: 320,
            render: (_, u) => (
                <div className="permission-user">
                    <Text strong>{u.displayName}</Text>{u.uid === principal.uid && <Tag style={{ marginInlineStart: 8 }}>You</Tag>}
                    <br /><Text type="secondary" style={{ fontSize: 12 }}>{u.email}</Text>
                </div>
            )
        },
        { title: 'State', dataIndex: 'status', key: 'status', width: 100, render: (s) => <StatusTag className="user-state-tag" kind={STATUS_KIND.USER} code={s} color={STATUS_COLOUR[s]} /> },
        { title: 'Roles', dataIndex: 'roles', key: 'roles', width: 140, render: (list) => (list.length ? list.map((r) => <Tag key={r}>{r}</Tag>) : <Text type="secondary">none</Text>) },
        { title: 'Authenticator', dataIndex: 'mfa', key: 'mfa', width: 160, render: (m) => MFA_LABEL[m] ?? m },
        { title: 'Invited', dataIndex: 'invitedAt', key: 'invitedAt', width: 130, render: at },
        {
            title: '',
            key: 'actions',
            width: 45,
            render: (_, u) => (
                <Dropdown trigger={['click']} menu={{ items: actionsFor(u), onClick: ({ key }) => onAction(u, key) }}>
                    <Button type="text" icon={<EllipsisOutlined />} aria-label={'Actions for ' + u.displayName} />
                </Dropdown>
            )
        }
    ];

    const spec = reasoned ? REASONED[reasoned.kind] : null;

    return (
        <section className="panel permission-panel">
            <div className="permission-toolbar">
                <div className="permission-panel-heading">
                    <h3 className="panel-title">Users</h3>
                    <div className="permission-info-banner">
                        <ActionIcon name="info" />
                        <span>
                            Last refreshed {refreshedAt ? refreshedAt.toLocaleTimeString('en-GB', { timeZone: 'Asia/Colombo' }) : '—'}.
                            You cannot change your own access,another administrator can.
                        </span>
                    </div>
                </div>
                <Space className="permission-toolbar-actions">
                    <Button className="refresh-button" icon={<ActionIcon name="refresh" />} onClick={load} loading={loading}>Refresh</Button>
                    <Button type="primary" icon={<UserAddOutlined />} onClick={() => { setDialogError(null); setInviting(true); }}>Invite User</Button>
                </Space>
            </div>
            {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
            <Table className="permission-table users-table" rowKey="uid" size="small" loading={loading} columns={columns}
                dataSource={loading && users.length === 0 ? undefined : users} tableLayout="fixed" scroll={{ x: 895 }}
                pagination={{ pageSize: 25, hideOnSinglePage: true }} locale={{ emptyText: 'No users yet' }} />

            <Modal open={inviting} width={540} title="Invite User" onCancel={() => setInviting(false)} onOk={() => inviteForm.submit()}
                okText="Send Invite" okButtonProps={{ loading: busy }} rootClassName="lfoc-styles-wrapper user-invite-modal" destroyOnHidden>
                <div className="permission-info-banner permission-modal-info">
                    <ActionIcon name="info" />
                    <span>
                        They receive an email with a temporary password, valid for 3 days. At their first sign-in they choose a
                        password and set up an authenticator app.
                    </span>
                </div>
                {dialogError && <Alert type="error" showIcon message={dialogError} className="auth-alert" />}
                <Form className="invite-user-form" form={inviteForm} layout="vertical"
                    requiredMark={(label, { required }) => (
                        <>
                            {label}
                            {required && <span className="invite-required-mark">*</span>}
                        </>
                    )}
                    onFinish={invite}>
                    <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Enter a valid email address.' }]}><Input /></Form.Item>
                    <Form.Item name="firstName" label="First Name" rules={NAME_RULES('First name')}><Input maxLength={50} /></Form.Item>
                    <Form.Item name="lastName" label="Last Name" rules={NAME_RULES('Last name')}><Input maxLength={50} /></Form.Item>
                    <Form.Item name="roles" label="Roles" rules={[{ required: true, message: 'Choose at least one role.' }]}>
                        <Select mode="multiple" options={roleOptions} placeholder="Select" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal open={Boolean(editing)} title={'Roles for ' + (editing?.displayName ?? '')} onCancel={() => setEditing(null)} onOk={() => rolesForm.submit()}
                okText="Save" okButtonProps={{ loading: busy }} rootClassName="lfoc-styles-wrapper" destroyOnHidden>
                {dialogError && <Alert type="error" showIcon message={dialogError} className="auth-alert" />}
                <Form form={rolesForm} layout="vertical" onFinish={saveRoles}>
                    <Form.Item name="roles" label="Roles" extra="The change applies from their next request. With no role they see nothing.">
                        <Select mode="multiple" options={roleOptions} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal open={Boolean(reasoned)} title={spec?.title} onCancel={() => setReasoned(null)} onOk={() => reasonForm.submit()}
                okText={spec?.ok} okButtonProps={{ danger: reasoned?.kind !== 'reinstate', loading: busy }} rootClassName="lfoc-styles-wrapper" destroyOnHidden>
                <p><b>{reasoned?.user.displayName}</b> ({reasoned?.user.email})</p>
                <p className="panel-hint">{spec?.body}</p>
                {dialogError && <Alert type="error" showIcon message={dialogError} className="auth-alert" />}
                <Form form={reasonForm} layout="vertical" onFinish={confirmReasoned}>
                    <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Say why - it is kept in the audit trail.' }]}>
                        <Input.TextArea rows={3} maxLength={1000} />
                    </Form.Item>
                </Form>
            </Modal>
        </section>
    );
};

export default UsersPanel;
